import { useCallback, useEffect } from 'react';
import { Form } from 'antd';
import { dateFormatter } from 'constants/index';
import type { InboundFilters, OutboundFilters } from 'models/Warehouse';

interface UseFilterPanelProps {
  open: boolean;
  type: 'inbound' | 'outbound';
  initialInboundFilters?: InboundFilters;
  initialOutboundFilters?: OutboundFilters;
  onApplyInbound?: (filters: InboundFilters) => void;
  onApplyOutbound?: (filters: OutboundFilters) => void;
}

export const useFilterPanel = ({
  open,
  type,
  initialInboundFilters = {},
  initialOutboundFilters = {},
  onApplyInbound,
  onApplyOutbound,
}: UseFilterPanelProps) => {
  const [form] = Form.useForm();
  const isInbound = type === 'inbound';

  // Load initial filter values when drawer opens or type changes
  useEffect(() => {
    if (open) {
      if (isInbound && initialInboundFilters) {
        form.setFieldsValue({
          supplier: initialInboundFilters.supplierId,
          status: initialInboundFilters.status,
          showCompleted: initialInboundFilters.showCompleted,
        });
      } else if (!isInbound && initialOutboundFilters) {
        form.setFieldsValue({
          client: initialOutboundFilters.clientId,
          status: initialOutboundFilters.status,
          showCompleted: initialOutboundFilters.showCompleted,
        });
      }
    }
  }, [open, type, initialInboundFilters, initialOutboundFilters, form, isInbound]);

  const handleApply = useCallback(() => {
    const values = form.getFieldsValue();

    if (isInbound && onApplyInbound) {
      const inboundFilters: InboundFilters = {
        uuid: values.orderId || undefined,
        supplierId: values.supplier || undefined,
        status: values.status || undefined,
        showCompleted: values.showCompleted || false,
      };
      if (values.expectedArrivalDate?.[0]) {
        inboundFilters.expectedArrivalDateFrom = dateFormatter(values.expectedArrivalDate[0].toDate(), 'start');
      }
      if (values.expectedArrivalDate?.[1]) {
        inboundFilters.expectedArrivalDateTo = dateFormatter(values.expectedArrivalDate[1].toDate(), 'end');
      }
      if (values.shipmentDate?.[0]) {
        inboundFilters.shipmentDateFrom = dateFormatter(values.shipmentDate[0].toDate(), 'start');
      }
      if (values.shipmentDate?.[1]) {
        inboundFilters.shipmentDateTo = dateFormatter(values.shipmentDate[1].toDate(), 'end');
      }
      onApplyInbound(inboundFilters);
    } else if (!isInbound && onApplyOutbound) {
      const outboundFilters: OutboundFilters = {
        orderId: values.orderId || undefined,
        clientId: values.client || undefined,
        status: values.status || undefined,
        showCompleted: values.showCompleted || false,
      };
      if (values.expectedDeliveryDate?.[0]) {
        outboundFilters.expectedDeliveryDateFrom = dateFormatter(values.expectedDeliveryDate[0].toDate(), 'start');
      }
      if (values.expectedDeliveryDate?.[1]) {
        outboundFilters.expectedDeliveryDateTo = dateFormatter(values.expectedDeliveryDate[1].toDate(), 'end');
      }
      onApplyOutbound(outboundFilters);
    }
  }, [form, isInbound, onApplyInbound, onApplyOutbound]);

  const handleClear = useCallback(() => {
    form.resetFields();
  }, [form]);

  return {
    form,
    isInbound,
    handleApply,
    handleClear,
  };
};
